import { Engine } from "threngine";
import { Controller3D } from "./3DController";

import { Matrix4, Vector3, Object3D } from "three";
import { isMobile } from "react-device-detect";
import { CameraController } from "threngine/dist/types/src/core/render/input/cameraController";
import { DeviceOrientationControls } from "./DeviceOrientationControls";
import { EventEmitter } from "events";

export class DeviceOrientationCameraController {
  private hasGyroscope:boolean;
  private connected:boolean = false;
  private _enabled:boolean = false;
  private engine:Engine;
  private controller3D:Controller3D;
  private cameraController:CameraController;
  private originalCameraControllerUpdate;
  private _deviceOrientationControls:DeviceOrientationControls;
  private userSettingEnabled:boolean = true;

  public get enabled():boolean { return this._enabled; }
  public get deviceOrientationControls():DeviceOrientationControls { return this._deviceOrientationControls; }

  public static ENABLE_CHANGE:string = "enableChange";
  public static event:EventEmitter = new EventEmitter();

  public constructor(engine:Engine, controller3D:Controller3D) {
    this.hasGyroscope = isMobile;
    if (!this.hasGyroscope)
      return;

    this.controller3D = controller3D;
    this.engine = engine;
    this.cameraController = engine.services.render.cameraController;
    this.originalCameraControllerUpdate = this.cameraController.update;

    this._deviceOrientationControls = new DeviceOrientationControls(this.engine.services.render.camera);
  }

  public toggle():void {
    if (this._enabled) {
      this.disable();
      this.userSettingEnabled = false;
    } else {
      this.enable();
      this.userSettingEnabled = true;
    }
  }

  private connect = ():Promise<boolean> => {
    if (!this.connected) {
      return this._deviceOrientationControls.connect().then(() => {
        this.connected = true;
        return Promise.resolve(true);
      }).catch(() => {
        return Promise.resolve(false);
      })
    }

    return Promise.resolve(true);
  }

  private enable = async () => {
    if (!this.hasGyroscope || !this.controller3D.isInterior() || this._enabled)
      return Promise.resolve();
    
    const connected:boolean = await this.connect();
    // TODO: let the user know that they have to allow the gyroscope usage
    // for this feature to work.
    if (!connected)
      return Promise.resolve();

    // TODO: this is a bit unclean... what we really want to do
    // is to tell the device orientation controller to reset the initial x-Rotation and y-Rotation.
    const gyroTransform = new Object3D();
    this._deviceOrientationControls = new DeviceOrientationControls(gyroTransform);
    this._deviceOrientationControls.connect();

    this._enabled = true;
    DeviceOrientationCameraController.event.emit(
      DeviceOrientationCameraController.ENABLE_CHANGE);

    const camera = this.engine.services.render.camera;
    const orbitControls = (this.cameraController as any)._controls;

    // small timeout to make sure the gyroscope transform is sane...
    setTimeout(() => {
      this.cameraController.update = () => {
        let originalMinPolarAngle = orbitControls.minPolarAngle;
        let originalMaxPolarAngle = orbitControls.maxPolarAngle;
        let originalMinAzimuthAngle = orbitControls.minAzimuthAngle;
        let originalMaxAzimuthAngle = orbitControls.maxAzimuthAngle;    

        orbitControls.minPolarAngle = 0;
        orbitControls.maxPolarAngle = Math.PI;
        orbitControls.minAzimuthAngle = -Infinity;
        orbitControls.maxAzimuthAngle = +Infinity;

        orbitControls.update();

        orbitControls.minPolarAngle = originalMinPolarAngle;
        orbitControls.maxPolarAngle = originalMaxPolarAngle;
        orbitControls.minAzimuthAngle = originalMinAzimuthAngle;
        orbitControls.maxAzimuthAngle = originalMaxAzimuthAngle;

        this._deviceOrientationControls.update();
        camera.rotation.x += gyroTransform.rotation.x;
        camera.rotation.y += gyroTransform.rotation.y;
        camera.rotation.z  = gyroTransform.rotation.z;
      }
    }, 150);
  }

  private disable = () => {
    if (!this.hasGyroscope || !this.controller3D.isInterior() || !this._enabled)
      return Promise.resolve();
    
    const camera = this.engine.services.render.camera;

    // TODO: using just the sign isn't necessarily the best choice...
    const step = camera.rotation.z * 0.1;
    return new Promise<void>((resolve) => {
      this.cameraController.update = () => {
        camera.rotation.z -= step;
        if (Math.abs(camera.rotation.z) <= Math.abs(step)) {
          camera.rotation.z = 0;
  
          const mat = new Matrix4().makeRotationFromEuler(camera.rotation);
          const orbitControls = (this.cameraController as any)._controls;
          orbitControls._target0 = new Vector3(0, 0, -1)
            .applyMatrix4(mat)
            .multiplyScalar(0.0001)
            .add(camera.position);
          orbitControls._position0 = camera.position;
          orbitControls.reset();
          this.cameraController.update = this.originalCameraControllerUpdate;
  
          this._enabled = false;
          DeviceOrientationCameraController.event.emit(
            DeviceOrientationCameraController.ENABLE_CHANGE);
          resolve();
        }
      };
    });
  }

  public async onBeforeEnterInterior():Promise<void> {
    // return;

    if (!this.hasGyroscope)
      return Promise.resolve();

    // Gyroscope permissions must be asked for through a click event handler on iOS.
    // If we try to connect from the event, it will be too late and not work.
    await this.connect();

    const callback = () => {
      Controller3D.event.removeListener(Controller3D.EVENT_CHANGED_CAM_FINISH, callback);
      if (this.connected && this.controller3D.isInterior() && this.userSettingEnabled) {
        this.enable();
      }
    };
    Controller3D.event.addListener(Controller3D.EVENT_CHANGED_CAM_FINISH, callback);
  }

  public async onBeforeLeaveInterior():Promise<void> {
    // return Promise.resolve();

    return this.disable();
  }
}